<template>
  <div class="audit-page">
    <section class="audit-hero">
      <div class="audit-wrap">
        <div class="audit-badge">
          <i class="audit-pulse"></i>{{ localize('AUDIT_BADGE') }}
        </div>
        <h1 class="audit-title">{{ localize('AUDIT_TITLE') }}</h1>
        <p class="audit-subtitle">{{ localize('AUDIT_SUBTITLE') }}</p>
      </div>
    </section>

    <section class="audit-form-section">
      <div class="audit-wrap">

        <!-- ---------- confidentiality ---------- -->
        <div class="privacy">
          <p class="privacy-text">{{ localize('AUDIT_PRIVACY') }}</p>
          <p class="privacy-ask">
            {{ localize('AUDIT_PRIVACY_ASK') }}
            <a href="mailto:contact@compdeep.com">contact@compdeep.com</a>
          </p>
        </div>

        <!-- ---------- progress ---------- -->
        <div class="steps" v-if="!success">
          <button
            v-for="(p, i) in pages"
            :key="p.key"
            type="button"
            class="step"
            :class="{ 'step-active': i === step, 'step-done': i < step }"
            @click="goTo(i)"
          >
            <span class="step-num">{{ i + 1 }}</span>
            <span class="step-name">{{ t(p.title) }}</span>
          </button>
        </div>
        <div class="progress-track" v-if="!success">
          <div class="progress-fill" :style="{ width: progress }"></div>
        </div>

        <div class="audit-card">

          <!-- ---------- submitted ---------- -->
          <div v-if="success" class="done-block">
            <div class="done-mark">✓</div>
            <p class="done-text">{{ localize('AUDIT_FORM_SUCCESS') }}</p>
          </div>

          <!-- ---------- questionnaire ---------- -->
          <form v-else @submit.prevent="onSubmit">
            <div class="page-head">
              <span class="page-count">{{ localize('AUDIT_STEP_OF', String(step + 1), String(pages.length)) }}</span>
              <h2 class="page-title">{{ t(page.title) }}</h2>
              <p class="page-blurb">{{ t(page.blurb) }}</p>
            </div>

            <div v-for="(q, qi) in page.questions" :key="q.key" class="q">
              <div class="q-head">
                <span class="q-num">{{ offset + qi + 1 }}</span>
                <span class="q-label">
                  {{ t(q.label) }}
                  <span v-if="q.optional" class="q-optional">{{ localize('AUDIT_OPTIONAL') }}</span>
                </span>
              </div>
              <p v-if="q.hint" class="q-hint">{{ t(q.hint) }}</p>

              <!-- free text groups -->
              <div v-if="q.type === 'fields'" class="field-row">
                <label v-for="f in q.fields" :key="f.key" class="field">
                  <span class="field-label">
                    {{ t(f.label) }}<em v-if="f.required">*</em>
                  </span>
                  <input
                    :type="f.type === 'email' ? 'email' : 'text'"
                    class="field-input"
                    :class="{ 'field-invalid': invalid.indexOf(q.key + '.' + f.key) !== -1 }"
                    v-model="answers[q.key][f.key]"
                    @input="clearInvalid(q.key + '.' + f.key)"
                  >
                </label>
              </div>

              <!-- dropdown groups -->
              <div v-else-if="q.type === 'selects'" class="field-row">
                <label v-for="f in q.fields" :key="f.key" class="field">
                  <span class="field-label">{{ t(f.label) }}</span>
                  <select class="field-input" v-model="answers[q.key][f.key]">
                    <option value="">{{ localize('AUDIT_SELECT') }}</option>
                    <option v-for="o in f.options" :key="o.v" :value="o.v">{{ t(o) }}</option>
                  </select>
                </label>
              </div>

              <!-- single dropdown -->
              <div v-else-if="q.type === 'select'" class="field-row">
                <label class="field field-single">
                  <select class="field-input" v-model="answers[q.key]">
                    <option value="">{{ localize('AUDIT_SELECT') }}</option>
                    <option v-for="o in q.options" :key="o.v" :value="o.v">{{ t(o) }}</option>
                  </select>
                </label>
              </div>

              <!-- pick one -->
              <div v-else-if="q.type === 'radio'" class="pills">
                <button
                  v-for="o in q.options"
                  :key="o.v"
                  type="button"
                  class="pill"
                  :class="{ 'pill-on': answers[q.key] === o.v }"
                  :title="o.alt ? t(o.alt) : null"
                  @click="answers[q.key] = answers[q.key] === o.v ? '' : o.v"
                >{{ t(o) }}</button>
              </div>

              <!-- pick many -->
              <div v-else-if="q.type === 'chips'" class="pills">
                <button
                  v-for="o in q.options"
                  :key="o.v"
                  type="button"
                  class="pill"
                  :class="{ 'pill-on': answers[q.key].indexOf(o.v) !== -1 }"
                  :title="o.alt ? t(o.alt) : null"
                  @click="toggle(q.key, o.v)"
                >{{ t(o) }}</button>
              </div>
            </div>

            <!-- ---------- closing block ---------- -->
            <div v-if="isLast" class="closing">
              <div class="q">
                <div class="q-head">
                  <span class="q-num q-num-plain">+</span>
                  <span class="q-label">{{ t(closing.notes.label) }}</span>
                </div>
                <p class="q-hint">{{ t(closing.notes.hint) }}</p>
                <textarea class="field-input field-textarea" rows="4" v-model="notes"></textarea>
              </div>

              <label class="consent" :class="{ 'consent-invalid': invalid.indexOf('consent') !== -1 }">
                <input type="checkbox" v-model="consent" @change="clearInvalid('consent')">
                <span>{{ t(closing.consent.label) }}</span>
              </label>
            </div>

            <p v-if="error" class="form-error">{{ error }}</p>

            <div class="nav">
              <button v-if="step > 0" type="button" class="btn btn-back" @click="back">
                {{ localize('AUDIT_BACK') }}
              </button>
              <button v-if="!isLast" type="button" class="btn btn-next" @click="next">
                {{ localize('AUDIT_NEXT') }}
              </button>
              <button v-else type="submit" class="btn btn-next" :disabled="sending">
                {{ sending ? localize('AUDIT_FORM_SENDING') : localize('AUDIT_FORM_SUBMIT') }}
              </button>
            </div>

          </form>
        </div>
      </div>
    </section>
  </div>
</template>
<script>
import {computed, reactive, ref} from "vue";
import {CLOSING, PAGES} from "@/stores/audit_form";
import Axios from "axios";
import {translationStore} from "@/stores/translation";

const blankAnswers = function () {

        const out = {};
        PAGES.forEach(function (p) {

            p.questions.forEach(function (q) {

                if (q.type === "fields" || q.type === "selects") {

                    out[q.key] = {};
                    q.fields.forEach(function (f) {

                        out[q.key][f.key] = "";

                    });

                } else if (q.type === "chips") {

                    out[q.key] = [];

                } else {

                    out[q.key] = "";

                }

            });

        });
        return out;

    },

    // the summary that lands in the inbox — always English, whatever the visitor read
    summarize = function (answers, notes) {

        const lines = [];
        PAGES.forEach(function (p) {

            lines.push("");
            lines.push(`== ${p.title.en} ==`);
            p.questions.forEach(function (q) {

                const a = answers[q.key];
                if (q.type === "fields" || q.type === "selects") {

                    q.fields.forEach(function (f) {

                        const v = a[f.key];
                        if (!v) {

                            return;

                        }
                        const o = f.options && f.options.find((x) => x.v === v);
                        lines.push(`${f.label.en}: ${o ? o.en : v}`);

                    });

                } else if (q.type === "chips") {

                    if (a.length) {

                        const picked = a.map(function (v) {

                            const o = q.options.find((x) => x.v === v);
                            return o ? o.en : v;

                        });
                        lines.push(`${q.label.en}: ${picked.join(", ")}`);

                    }

                } else if (a) {

                    const o = q.options.find((x) => x.v === a);
                    lines.push(`${q.label.en}: ${o ? o.en : a}`);

                }

            });

        });
        if (notes) {

            lines.push("");
            lines.push(`== ${CLOSING.notes.label.en} ==`);
            lines.push(notes);

        }
        return lines.join("\n").trim();

    };

export default {
    "name": "Audit",
    setup () {

        const translation = translationStore(),
            localize = function (...args) {

                return translation.localize(...args);

            },
            t = function (o) {

                if (!o) {

                    return "";

                }
                const lang = translation.VUE_APP_USER_LANGUAGE || "en";
                return o[lang] || o.en;

            },

            pages = PAGES,
            closing = CLOSING,
            step = ref(0),
            answers = reactive(blankAnswers()),
            notes = ref(""),
            consent = ref(false),
            invalid = ref([]),
            sending = ref(false),
            success = ref(false),
            error = ref(""),

            page = computed(() => pages[step.value]),
            isLast = computed(() => step.value === pages.length - 1),
            offset = computed(function () {

                let n = 0;
                for (let i = 0; i < step.value; i++) {

                    n += pages[i].questions.length;

                }
                return n;

            }),
            progress = computed(() => `${(step.value + 1) / pages.length * 100}%`),

            toggle = function (key, value) {

                const list = answers[key],
                    at = list.indexOf(value);
                if (at === -1) {

                    list.push(value);

                } else {

                    list.splice(at, 1);

                }

            },

            clearInvalid = function (key) {

                const at = invalid.value.indexOf(key);
                if (at === -1) {

                    return;

                }
                invalid.value.splice(at, 1);
                if (!invalid.value.length) {

                    error.value = "";

                }

            },

            toTop = function () {

                try {

                    document.querySelector(".audit-form-section").scrollIntoView({"behavior": "smooth",
                        "block": "start"});

                } catch (e) {

                    window.scrollTo(0, 0);

                }

            },

            validate = function () {

                const bad = [];
                if (step.value === 0) {

                    if (!answers.contact.name) {

                        bad.push("contact.name");

                    }
                    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(answers.contact.email)) {

                        bad.push("contact.email");

                    }
                    if (!answers.org.company) {

                        bad.push("org.company");

                    }

                }
                if (isLast.value && !consent.value) {

                    bad.push("consent");

                }
                invalid.value = bad;
                if (bad.length) {

                    error.value = bad.indexOf("consent") !== -1 && bad.length === 1
                        ? localize("AUDIT_CONSENT_REQUIRED")
                        : localize("AUDIT_REQUIRED");
                    return false;

                }
                error.value = "";
                return true;

            },

            next = function () {

                if (!validate()) {

                    return;

                }
                step.value += 1;
                toTop();

            },

            back = function () {

                error.value = "";
                step.value -= 1;
                toTop();

            },

            goTo = function (i) {

                if (i === step.value) {

                    return;

                }
                if (i > step.value && !validate()) {

                    return;

                }
                step.value = i;
                toTop();

            },

            onSubmit = async function () {

                if (!validate()) {

                    return;

                }
                sending.value = true;
                error.value = "";
                try {

                    await Axios.post(
                        `${process.env.VUE_APP_SERVER_URI || "/"}contact`,
                        {"name": answers.contact.name,
                            "email": answers.contact.email,
                            "company": answers.org.company,
                            "role": answers.contact.role,
                            "message": summarize(answers, notes.value),
                            "type": "audit"}
                    );
                    success.value = true;
                    toTop();

                } catch (e) {

                    error.value = e.response?.data?.message || localize("AUDIT_FORM_ERROR");

                }
                sending.value = false;

            };

        return {answers,
            back,
            clearInvalid,
            closing,
            consent,
            error,
            goTo,
            invalid,
            isLast,
            localize,
            next,
            notes,
            offset,
            onSubmit,
            page,
            pages,
            progress,
            sending,
            step,
            success,
            t,
            toggle};

    }
};
</script>
<style scoped>
.audit-page {
  font-family: var(--font-body);
  color: var(--text-primary);
  background: var(--bg-primary);
  font-size: 17px;
}

.audit-hero {
  padding: 160px 56px 40px;
  text-align: center;
}

.audit-wrap {
  max-width: 880px;
  margin: 0 auto;
}

.audit-badge {
  display: inline-flex;
  align-items: center;
  padding: 10px 28px;
  margin-bottom: 24px;
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: #fff;
  background: linear-gradient(135deg, #fb7185 0%, #f43f5e 45%, #ec4899 100%);
  border-radius: 30px;
}

.audit-pulse {
  width: 6px;
  height: 6px;
  margin-right: 10px;
  border-radius: 50%;
  background: #fff;
  animation: audit-pulse 2.4s ease-out infinite;
}

@keyframes audit-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(255,255,255,0.7); }
  70%  { box-shadow: 0 0 0 7px rgba(255,255,255,0); }
  100% { box-shadow: 0 0 0 0 rgba(255,255,255,0); }
}

.audit-title {
  font-family: var(--font-heading);
  font-size: 2.6em;
  font-weight: 800;
  line-height: 1.12;
  margin: 0 0 20px;
  background: linear-gradient(135deg, #fb7185 0%, #f43f5e 45%, #ec4899 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.audit-subtitle {
  font-size: 1.1em;
  color: var(--text-secondary);
  line-height: 1.8;
  margin: 0 auto;
  max-width: 640px;
}

.audit-form-section {
  padding: 40px 56px 120px;
  scroll-margin-top: 90px;
}

/* ---------- confidentiality ---------- */
.privacy {
  margin-bottom: 28px;
  padding: 22px 26px;
  border: 1px solid var(--border-subtle);
  border-left: 3px solid var(--accent-blue);
  border-radius: 14px;
  background: var(--bg-card);
}

.privacy-text {
  margin: 0;
  font-size: 0.98em;
  line-height: 1.75;
  color: var(--text-secondary);
}

.privacy-ask {
  margin: 10px 0 0;
  font-size: 0.98em;
  line-height: 1.75;
  color: var(--text-secondary);
}

.privacy-ask a {
  color: var(--accent-blue);
  font-weight: 600;
  text-decoration: none;
}

.privacy-ask a:hover { text-decoration: underline; }

/* ---------- progress ---------- */
.steps {
  display: flex;
  gap: 10px;
  margin-bottom: 14px;
}

.step {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border: 1px solid var(--border-subtle);
  border-radius: 14px;
  background: var(--bg-card);
  color: var(--text-secondary);
  font-family: var(--font-body);
  font-size: 0.92em;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.25s ease, color 0.25s ease, background 0.25s ease;
}

.step:hover { border-color: rgba(99,102,241,0.35); }

.step-num {
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 700;
  color: var(--text-secondary);
  background: var(--bg-input);
}

.step-active {
  color: var(--text-primary);
  border-color: rgba(99,102,241,0.45);
  background: rgba(99,102,241,0.06);
}

.step-active .step-num,
.step-done .step-num {
  color: #fff;
  background: linear-gradient(135deg, #7c3aed, #6366f1 55%, #06b6d4);
}

.step-done { color: var(--text-primary); }

.progress-track {
  height: 4px;
  border-radius: 4px;
  background: var(--bg-input);
  overflow: hidden;
  margin-bottom: 28px;
}

.progress-fill {
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(90deg, #7c3aed, #6366f1 55%, #06b6d4);
  transition: width 0.35s ease;
}

/* ---------- card ---------- */
.audit-card {
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: 24px;
  padding: 44px 48px;
  box-shadow: var(--shadow-md);
}

.page-head { margin-bottom: 36px; }

.page-count {
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--text-secondary);
}

.page-title {
  font-family: var(--font-heading);
  font-size: 1.7em;
  font-weight: 800;
  margin: 10px 0 10px;
  /* opt out of the global gradient heading treatment */
  background: none;
  color: var(--text-primary);
  -webkit-text-fill-color: var(--text-primary);
}

.page-blurb {
  font-size: 1em;
  color: var(--text-secondary);
  line-height: 1.7;
  margin: 0;
}

/* ---------- question ---------- */
.q {
  padding: 26px 0;
  border-top: 1px solid var(--border-subtle);
}

.q-head {
  display: flex;
  align-items: baseline;
  gap: 14px;
}

.q-num {
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 700;
  color: var(--accent-blue);
  background: rgba(99,102,241,0.1);
}

.q-num-plain { color: var(--text-secondary); background: var(--bg-input); }

.q-label {
  font-family: var(--font-heading);
  font-size: 1.08em;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.5;
}

.q-optional {
  margin-left: 10px;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--text-secondary);
}

.q-hint {
  margin: 8px 0 0 40px;
  font-size: 0.95em;
  color: var(--text-secondary);
  line-height: 1.7;
}

/* ---------- inputs ---------- */
.field-row {
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin: 16px 0 0 40px;
  max-width: 480px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-single { max-width: 480px; }

.field-label {
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--text-secondary);
}

.field-label em { color: var(--accent-blue); font-style: normal; margin-left: 4px; }

.field-input {
  font-family: var(--font-body);
  font-size: 1.02em;
  color: var(--text-primary);
  background: var(--bg-primary);
  border: 1px solid var(--border-subtle);
  border-radius: 12px;
  padding: 14px 16px;
  outline: none;
  width: 100%;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

select.field-input { cursor: pointer; }

.field-input:focus {
  border-color: rgba(99,102,241,0.5);
  box-shadow: 0 0 0 3px rgba(99,102,241,0.13);
}

.field-invalid {
  border-color: #dc2626;
  box-shadow: 0 0 0 3px rgba(220,38,38,0.12);
}

.field-textarea {
  margin: 16px 0 0 40px;
  width: calc(100% - 40px);
  resize: vertical;
  line-height: 1.7;
}

/* ---------- pills ---------- */
.pills {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 16px 0 0 40px;
}

.pill {
  font-family: var(--font-body);
  font-size: 0.96em;
  color: var(--text-secondary);
  background: var(--bg-input);
  border: 1px solid var(--border-subtle);
  border-radius: 30px;
  padding: 10px 18px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pill:hover {
  color: var(--text-primary);
  border-color: rgba(99,102,241,0.45);
  background: var(--bg-card-hover);
}

.pill-on {
  color: #fff;
  border-color: transparent;
  background: linear-gradient(135deg, #fb7185 0%, #f43f5e 45%, #ec4899 100%);
  box-shadow: 0 6px 20px rgba(244,63,94,0.28);
}

/* ---------- closing ---------- */
.closing { border-top: 1px solid var(--border-subtle); }

.consent {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 8px 0 0;
  padding: 18px 20px;
  border: 1px solid var(--border-subtle);
  border-radius: 14px;
  background: var(--bg-input);
  font-size: 0.98em;
  line-height: 1.65;
  color: var(--text-secondary);
  cursor: pointer;
}

.consent input {
  margin-top: 3px;
  width: 18px;
  height: 18px;
  accent-color: var(--accent-blue);
  cursor: pointer;
}

.consent-invalid {
  border-color: #dc2626;
  background: rgba(220,38,38,0.05);
}

/* ---------- nav ---------- */
.nav {
  display: flex;
  gap: 14px;
  justify-content: flex-end;
  margin-top: 32px;
}

.btn {
  font-family: var(--font-heading);
  font-size: 1.02em;
  font-weight: 700;
  border-radius: 30px;
  padding: 15px 44px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease;
}

.btn-back {
  color: var(--text-secondary);
  background: transparent;
  border-color: var(--border-subtle);
}

.btn-back:hover { color: var(--text-primary); border-color: var(--border-hover); }

.btn-next {
  color: #fff;
  background: linear-gradient(135deg, #fb7185 0%, #f43f5e 45%, #ec4899 100%);
  box-shadow: 0 10px 32px rgba(244,63,94,0.3);
}

.btn-next:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 16px 44px rgba(244,63,94,0.4);
}

.btn-next:disabled { opacity: 0.6; cursor: default; }

.form-error {
  color: #dc2626;
  font-weight: 600;
  font-size: 1em;
  margin: 22px 0 0;
  text-align: right;
}

/* ---------- submitted ---------- */
.done-block { text-align: center; padding: 40px 0; }

.done-mark {
  width: 64px;
  height: 64px;
  margin: 0 auto 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8em;
  color: #fff;
  border-radius: 50%;
  background: linear-gradient(135deg, #fb7185 0%, #f43f5e 45%, #ec4899 100%);
  box-shadow: 0 12px 36px rgba(244,63,94,0.3);
}

.done-text {
  font-size: 1.15em;
  color: var(--text-primary);
  line-height: 1.8;
  margin: 0 auto;
  max-width: 460px;
}

@media (max-width: 900px) {
  .steps { gap: 8px; }
  .step-name { display: none; }
  .step { flex: 0 0 auto; justify-content: center; padding: 12px 16px; }
}

@media (max-width: 680px) {
  .audit-hero { padding: 120px 20px 32px; }
  .audit-title { font-size: 2em; }
  .audit-form-section { padding: 32px 16px 80px; }
  .audit-card { padding: 28px 20px; border-radius: 18px; }
  .q-hint, .pills, .field-row { margin-left: 0; }
  .field-textarea { margin-left: 0; width: 100%; }
  .field { flex: 1 1 100%; }
  .nav { flex-direction: column-reverse; }
  .btn { width: 100%; }
  .form-error { text-align: center; }
}
</style>
